import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { RhEvent } from '../../../api/models/RhEvent';
import { routes } from '../../../route-list';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { StreamOnThirdPartyScreen } from '../components/StreamOnThirdPartyScreen';

export type StreamOnThirdPartyProps = {
  // Outputs
  /** current event based on param id */
  event: RhEvent | null;
  /** if currently loading event */
  isLoadingEvent: boolean;
  /** user has pressed join, and is waiting for stream key */
  isGettingMuxId: boolean;
  rtmpServerUrl: string | null;
  isEndingStream: boolean;
  hasEventPassed: boolean;

  // Inputs
  /** called when the user clicks the button */
  userClickedStartEvent: () => void;
  userClickedEndEvent: () => void;
};

/**
 * Why is this not following the same structure as (link below)?
 * https://github.com/smashingboxes/arno/blob/dev/src/modules/home/containers/HomeContainer.tsx
 *
 * Reason 1: Don’t call Hooks inside loops, conditions, or nested functions
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level)
 * Reason 2: Don’t call Hooks from regular JavaScript functions.
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-from-react-functions)
 */
export const StreamOnThirdPartyContainer = () => {
  const [isGettingMuxId, setIsGettingMuxId] = useState(false);
  const [isEndingStream, setIsEndingStream] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<RhEvent | null>(null);

  const request = useRequest();
  const params = useParams<{ id: string }>();
  const history = useHistory();

  // load event with param.id on mount
  const { loading: loadingEvent } = useAsyncRequest(
    useCallback(() => request<RhEvent>(apiConfigs.getEvent(params.id)), [params.id, request]),
    useCallback(result => {
      const event: RhEvent = result;
      setCurrentEvent(event);
    }, [])
  );

  const hasEventPassed = useMemo(() => {
    return currentEvent?.endTime ? dayjs(currentEvent.endTime).isBefore(new Date()) : false;
  }, [currentEvent?.endTime]);

  return (
    <StreamOnThirdPartyScreen
      event={currentEvent}
      isLoadingEvent={loadingEvent}
      isGettingMuxId={isGettingMuxId}
      rtmpServerUrl={process.env.REACT_APP_MUX_RTMP_SERVER_URL || null}
      isEndingStream={isEndingStream}
      hasEventPassed={hasEventPassed}
      userClickedStartEvent={async () => {
        try {
          if (!currentEvent?.muxID) {
            setIsGettingMuxId(true);
            // start event: update startTime, get muxId
            const result = await request<RhEvent>(apiConfigs.startEvent(params.id));
            setCurrentEvent(result);
            setIsGettingMuxId(false);
          }

          toast.info(`Event started, stream key generated!`, { closeOnClick: false });
        } catch (e: any) {
          toast.error('Failed to start event');
          setIsGettingMuxId(false);
        }
      }}
      userClickedEndEvent={async () => {
        try {
          setIsEndingStream(true);
          const result = await request<RhEvent>(apiConfigs.endEvent(params.id));
          setCurrentEvent(result);
          setIsEndingStream(false);
          toast.info(`Event ended!`, { closeOnClick: false });
          history.push(routes.EVENTS);
        } catch (e: any) {
          toast.error('Failed to end event');
          setIsEndingStream(false);
        }
      }}
    />
  );
};
